import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { usePaginatedQuery, useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Plus, Trash2, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/layout";
import { DeleteConfirmationDialog } from "@/components/delete-confirmation-dialog";
import { ProjectAccessDialog, ProjectCreateDialog } from "@/components/projects";
import { Button } from "@/components/ui/button";
import { useUserProfile } from "@/components/auth";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { EmptyProjectsState } from "@/components/empty-state";
import { formatDate } from "@/lib/utils";
import { useInfiniteScroll } from "@/lib/use-infinite-scroll";
import { useProject } from "@/components/projects";

const PAGE_SIZE = 40;

export const Route = createFileRoute("/")({
  component: ProjectsIndexPage,
});

export function ProjectsIndexPage() {
  const projectsQuery = usePaginatedQuery(
    api.projects.getWithItemCount,
    {},
    { initialNumItems: PAGE_SIZE }
  );
  const { userProfile: viewer } = useUserProfile();
  const deleteProject = useMutation(api.projects.remove);
  const {
    switchProject,
    setProjectId,
    projectId: activeProjectId,
    projects: allProjects,
  } = useProject();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(
    null
  );
  const [accessProject, setAccessProject] = useState<{
    _id: Id<"projects">;
    name: string;
    projectManagerUserId?: string;
  } | null>(null);
  const { data: organizationMembers } = useQuery(
    convexQuery(api.auth.listOrganizationMembersMinimal, {})
  );
  const organizationMemberNameById = useMemo(
    () =>
      new Map(
        (organizationMembers ?? []).map((member) => [
          member._id,
          member.name?.trim() || member.email,
        ])
      ),
    [organizationMembers]
  );

  const canManageProjectAccess =
    viewer?.orgRole === "owner" || viewer?.orgRole === "admin";

  const projects = projectsQuery.results;
  const isLoadingFirstPage =
    projectsQuery.status === "LoadingFirstPage" && projects.length === 0;
  const loadMoreRef = useInfiniteScroll(
    projectsQuery.status === "CanLoadMore",
    projectsQuery.loadMore,
    PAGE_SIZE
  );

  const handleDeleteProject = async (projectId: string) => {
    setDeletingProjectId(projectId);
    try {
      await deleteProject({
        projectId: projectId as Parameters<typeof deleteProject>[0]["projectId"],
      });

      if (activeProjectId === projectId) {
        const fallbackProject = allProjects?.find((project) => project._id !== projectId);
        if (fallbackProject) {
          switchProject(fallbackProject._id as Id<"projects">);
        } else {
          setProjectId(null);
        }
      }

      toast.success("Project deleted successfully");
    } catch (error) {
      toast.error("Failed to delete project");
      console.error("Delete error:", error);
    } finally {
      setDeletingProjectId(null);
    }
  };

  if (isLoadingFirstPage) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Projects"
          description="Manage your projects and organize submittals"
        />
        <div className="flex items-center justify-center p-12">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Projects"
        description="Manage your projects and organize submittals"
        actions={
          <ProjectCreateDialog
            open={isCreateDialogOpen}
            onOpenChange={setIsCreateDialogOpen}
            description="Create a new project to organize your submittals."
            showDetailsFields
            trigger={
              <Button>
                <Plus className="size-4" />
                Create Project
              </Button>
            }
          />
        }
      />

      {projects.length === 0 ? (
        <EmptyProjectsState onCreateNew={() => setIsCreateDialogOpen(true)} />
      ) : (
        <>
          <div className="rounded-lg border border-border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project Name</TableHead>
                  <TableHead>Project #</TableHead>
                  <TableHead>PM</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[180px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((project) => {
                  const submittalCount = project.itemCount ?? 0;
                  const documentCount = project.documentCount ?? 0;
                  const totalItems = submittalCount + documentCount;
                  const hasJobs = totalItems > 0;
                  const isDeleting = deletingProjectId === project._id;
                  const projectManagerName =
                    project.projectManagerName ??
                    (project.projectManagerUserId
                      ? organizationMemberNameById.get(project.projectManagerUserId)
                      : undefined);

                  return (
                    <TableRow key={project._id}>
                      <TableCell className="font-medium">
                        <button
                          onClick={() => {
                            switchProject(project._id as Id<"projects">, {
                              to: "/project",
                              title: "Project"
                            });
                          }}
                          title={project.name}
                          className="text-foreground hover:text-primary hover:underline underline-offset-4 transition-colors text-left"
                        >
                          {project.name}
                        </button>
                      </TableCell>
                      <TableCell className="max-w-[160px] truncate text-muted-foreground">
                        {project.projectNumber || "-"}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-muted-foreground">
                        {projectManagerName || "-"}
                      </TableCell>
                      <TableCell className="max-w-[260px] truncate text-muted-foreground">
                        {project.projectAddress || "-"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(project.createdAt)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          {canManageProjectAccess && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setAccessProject({
                                  _id: project._id as Id<"projects">,
                                  name: project.name,
                                  projectManagerUserId: project.projectManagerUserId,
                                })
                              }
                            >
                              <Users className="size-4" />
                              Access
                            </Button>
                          )}

                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-block">
                                  {hasJobs ? (
                                    <Button
                                      variant="ghost"
                                      size="icon-sm"
                                      className="text-muted-foreground cursor-not-allowed opacity-50"
                                      disabled
                                      aria-label={`Cannot delete ${project.name} - ${submittalCount} submittal(s) and ${documentCount} document(s) are attached`}
                                    >
                                      <Trash2 className="size-4" />
                                    </Button>
                                  ) : (
                                    <DeleteConfirmationDialog
                                      title="Delete Project"
                                      description={`Are you sure you want to delete "${project.name}"? This action cannot be undone.`}
                                      onConfirm={() =>
                                        handleDeleteProject(project._id)
                                      }
                                    >
                                      <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        className="text-muted-foreground hover:text-destructive"
                                        disabled={isDeleting}
                                        aria-label={`Delete ${project.name}`}
                                      >
                                        {isDeleting ? (
                                          <Loader2 className="size-4 animate-spin" />
                                        ) : (
                                          <Trash2 className="size-4" />
                                        )}
                                      </Button>
                                    </DeleteConfirmationDialog>
                                  )}
                                </span>
                              </TooltipTrigger>
                              {hasJobs && (
                                <TooltipContent>
                                  <p>
                                    Cannot delete while {submittalCount} submittal(s) and{" "}
                                    {documentCount} document(s) are attached.
                                  </p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div ref={loadMoreRef} className="flex min-h-10 items-center justify-center">
            {projectsQuery.status === "LoadingMore" && (
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            )}
          </div>
        </>
      )}

      {accessProject ? (
        <ProjectAccessDialog
          open={Boolean(accessProject)}
          onOpenChange={(open) => {
            if (!open) setAccessProject(null);
          }}
          projectId={accessProject._id as Id<"projects">}
          projectName={accessProject.name}
          lockedUserIds={
            accessProject.projectManagerUserId
              ? [accessProject.projectManagerUserId]
              : []
          }
          lockedUserLabel="project manager"
        />
      ) : null}
    </div>
  );
}
